import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, CheckCircle2, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import legacyCultivarImportService from '../../services/legacyCultivarImport';
import { errorMessage } from '../../utils/apiResponse';
import type { LegacyEvaluationCultivar, LegacyEvaluationPreview, LegacyEvaluationResult } from '../../types/legacyCultivarImport';

/**
 * TEMPORARY (Rob, 2026-10-16): the evaluations and photos of the cultivars
 * brought across on the legacy import page. Each legacy evaluation is
 * recorded on the cultivar imported from the same legacy cultivar. Deleted
 * with LegacyCultivarImport after 2026-10-28.
 */

const cardCls = 'bg-white border border-neutral-200 rounded-xl p-4 sm:p-6';
const errorCls = 'bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words';
const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
/** Asked again while the server says it ran out of time and made progress. */
const MAX_RETRIES = 5;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function resultLine(r: LegacyEvaluationResult): string {
  if (r.outcome === 'skipped') return r.reason ?? 'skipped';
  const parts = [
    `${r.created ?? 0} evaluation${r.created === 1 ? '' : 's'} added`,
    r.already ? `${r.already} already here` : null,
    r.photos_added ? plural(r.photos_added, 'photo') : null,
    r.failed ? `${r.failed} not imported` : null,
    r.photos_failed ? `${plural(r.photos_failed, 'photo')} not brought across` : null,
    r.not_reached ? `${r.not_reached} still to do: press Import again` : null,
  ].filter(Boolean);
  return parts.join(', ');
}

function LegacyEvaluationsSection({ refreshKey, fmt }: { refreshKey: number; fmt: (d: string) => string }) {
  const [preview, setPreview] = useState<LegacyEvaluationPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [progress, setProgress] = useState<{ done: number; of: number; name: string } | null>(null);
  const [results, setResults] = useState<LegacyEvaluationResult[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await legacyCultivarImportService.previewEvaluations();
      if (res.success && res.data) setPreview(res.data);
      else setError(errorMessage(res, 'Could not read the legacy evaluations'));
    } catch (e) {
      setError(errorMessage(e, 'Could not read the legacy evaluations'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const ready = useMemo(() => (preview?.cultivars ?? []).filter((c) => c.cultivar), [preview]);
  const waiting = useMemo(() => (preview?.cultivars ?? []).filter((c) => !c.cultivar), [preview]);
  const toDo = ready.filter((c) => c.imported < c.evaluations);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ready;
    return ready.filter((c) => [c.legacy_name, c.cult_id, c.cultivar?.name]
      .some((v) => v && v.toLowerCase().includes(q)));
  }, [ready, search]);
  const chosen = ready.filter((c) => selected.has(c.cult_id));

  const toggle = (id: string, on: boolean) => setSelected((s) => {
    const next = new Set(s);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  const runImport = async () => {
    if (!chosen.length) return;
    setError(null); setResults(null);
    const all: LegacyEvaluationResult[] = [];
    try {
      // One cultivar a request: each brings its photos, and a slow one must
      // not stop the rest.
      for (let i = 0; i < chosen.length; i += 1) {
        const c = chosen[i];
        setProgress({ done: i, of: chosen.length, name: c.cultivar?.name ?? c.legacy_name });
        let merged: LegacyEvaluationResult | null = null;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
          const res = await legacyCultivarImportService.importEvaluations([c.cult_id]);
          if (!res.success || !res.data) throw new Error(errorMessage(res, 'The import stopped part way'));
          const r = res.data.results[0];
          if (!r) break;
          merged = merged ? {
            ...r,
            created: (merged.created ?? 0) + (r.created ?? 0),
            already: Math.max(0, (r.already ?? 0) - (merged.created ?? 0)),
            photos_added: (merged.photos_added ?? 0) + (r.photos_added ?? 0),
          } : r;
          // Carry on only while the server ran out of time and got further.
          if (res.data.finished || r.outcome !== 'partial' || !(r.created || r.photos_added)) break;
        }
        if (merged) all.push(merged);
        setResults([...all]);
      }
    } catch (e) {
      setError(errorMessage(e, 'The import stopped part way'));
    } finally {
      setResults(all);
      setProgress(null);
      setSelected(new Set());
      load();
    }
  };

  const row = (c: LegacyEvaluationCultivar) => {
    const left = c.evaluations - c.imported;
    return (
      <li key={c.cult_id} className="py-3 flex items-start gap-3">
        <input
          type="checkbox"
          aria-label={`Import the evaluations of ${c.cultivar?.name ?? c.legacy_name}`}
          className="mt-1 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          checked={selected.has(c.cult_id)}
          onChange={(e) => toggle(c.cult_id, e.target.checked)}
        />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="min-w-0 text-sm font-medium text-neutral-800 break-words">
              {c.cultivar && <Link to={`/cultivar/${c.cultivar.id}?tab=observations`} className="text-primary-600 hover:underline">{c.cultivar.name}</Link>}
              <span className="ml-2 text-xs font-normal text-neutral-500 break-all">{c.legacy_name} · {c.cult_id}</span>
            </p>
            <span className={`inline-block shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${left > 0
              ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-neutral-50 text-neutral-500 border-neutral-200'}`}
            >
              {left > 0 ? `${left} to bring across` : 'All here'}
            </span>
          </div>
          <p className="text-xs text-neutral-500 break-words">
            {plural(c.evaluations, 'evaluation')} in the legacy system, {c.imported} here
            {c.last_date ? `; latest ${fmt(c.last_date)}` : ''}
          </p>
        </div>
      </li>
    );
  };

  const tally = (k: 'created' | 'photos_added') => (results ?? []).reduce((n, r) => n + (r[k] ?? 0), 0);

  return (
    <div className={`${cardCls} space-y-3`}>
      <h2 className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
        <Camera className="h-4 w-4 shrink-0 text-neutral-500" /> Evaluations and photos
      </h2>
      <p className="text-xs text-neutral-500">
        Each legacy evaluation is recorded on the cultivar you imported from the same legacy cultivar, with its photos.
        Brix, fruit size (as diameter in mm), background and flesh colour, flavour, crop load and stone become readings;
        skin and fruit colour, shape, texture, flower stage, the recommendation, rootstock, who recorded it and the
        comments go in the evaluation&rsquo;s notes. Running it again adds only what is missing.
      </p>

      {error && <div className={errorCls}>{error}</div>}

      {results && results.length > 0 && (
        <div className="space-y-2 rounded-lg border border-neutral-200 p-3">
          <p className="flex items-start gap-2 text-sm text-neutral-800">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
            <span className="min-w-0 break-words">
              {`${plural(tally('created'), 'evaluation')} and ${plural(tally('photos_added'), 'photo')} brought across.`}
            </span>
          </p>
          <ul className="text-xs text-neutral-600 space-y-1">
            {results.map((r) => (
              <li key={r.cult_id} className="break-words">
                <span className="font-medium">{r.name}</span>: {resultLine(r)}
                {(r.problems ?? []).length > 0 && (
                  <ul className="mt-0.5 ml-4 list-disc text-amber-800">
                    {(r.problems ?? []).slice(0, 10).map((p) => <li key={p}>{p}</li>)}
                    {(r.problems ?? []).length > 10 && <li>{`and ${(r.problems ?? []).length - 10} more`}</li>}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading && !preview && (
        <p className="flex items-center gap-2 text-sm text-neutral-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Reading the legacy evaluations…
        </p>
      )}

      {preview && (
        <>
          <p className="text-xs text-neutral-500 break-words">
            {`${plural(preview.cultivars.length, 'legacy cultivar')} with evaluations: ${ready.length} imported here (${toDo.length} with evaluations still to bring across), ${waiting.length} not imported yet.`}
          </p>

          {ready.length === 0 ? (
            <p className="text-sm text-neutral-500">
              None of the cultivars with evaluations has been imported yet. Import the cultivars above first.
            </p>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="search"
                  className={inputCls}
                  placeholder="Filter by name or code"
                  aria-label="Filter the cultivars with evaluations"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!toDo.length || !!progress}
                    onClick={() => setSelected(new Set(toDo.map((c) => c.cult_id)))}
                  >
                    Tick all still to do
                  </Button>
                  <Button onClick={runImport} disabled={!chosen.length || !!progress}>
                    {progress
                      ? `Cultivar ${progress.done + 1} of ${progress.of}…`
                      : `Import evaluations for ${plural(chosen.length, 'cultivar')}`}
                  </Button>
                </div>
              </div>
              {progress && (
                <p className="flex items-center gap-2 text-xs text-neutral-600 break-words">
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                  {`Bringing across ${progress.name} and its photos. A cultivar with many photos can take a minute or two.`}
                </p>
              )}
              {shown.length === 0
                ? <p className="text-sm text-neutral-500">Nothing matches &ldquo;{search.trim()}&rdquo;.</p>
                : <ul className="divide-y divide-neutral-100">{shown.map(row)}</ul>}
            </>
          )}

          {waiting.length > 0 && (
            <details className="text-xs text-neutral-600">
              <summary className="cursor-pointer text-neutral-700">
                {`${plural(waiting.length, 'cultivar')} with evaluations not imported yet`}
              </summary>
              <ul className="mt-2 space-y-0.5">
                {waiting.map((c) => (
                  <li key={c.cult_id} className="break-words">
                    {c.legacy_name} <span className="text-neutral-400 break-all">({c.cult_id})</span>: {plural(c.evaluations, 'evaluation')}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

export default LegacyEvaluationsSection;
